import { ResponsiveTable } from '../components/ResponsiveTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft, 
  User, 
  Mail, 
  Shield, 
  Building2,
  Key,
  Store,
  Bell
} from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { HelpIcon } from '../components/HelpIcon';
import { ConfirmModal } from '../components/ConfirmModal';

interface Company {
  id: string;
  name: string;
}

interface Shop {
  id: string;
  name: string;
  email?: string | null;
  companyId?: string | null;
}

interface AdminNotificationPreference {
  eventType:
    | 'booking_received'
    | 'booking_updated'
    | 'booking_deleted'
    | 'appointment_confirmation'
    | 'appointment_cancelled'
    | 'appointment_rescheduled'
    | 'appointment_reminder_d7'
    | 'appointment_reminder_d1'
    | 'appointment_reminder_h2';
  emailEnabled: boolean;
  smsEnabled: boolean;
  inAppEnabled: boolean;
}

const notificationEventLabels: Record<AdminNotificationPreference['eventType'], string> = {
  booking_received: 'Réservation reçue',
  booking_updated: 'Réservation modifiée',
  booking_deleted: 'Réservation annulée',
  appointment_confirmation: 'Confirmation de rendez-vous',
  appointment_cancelled: 'Rendez-vous annulé',
  appointment_rescheduled: 'Rendez-vous replanifié',
  appointment_reminder_d7: 'Rappel J-7',
  appointment_reminder_d1: 'Rappel J-1',
  appointment_reminder_h2: 'Rappel H-2',
};

const AdminEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [assignedShopIds, setAssignedShopIds] = useState<string[]>([]);
  const [savingShops, setSavingShops] = useState(false);
  const [shopSuccess, setShopSuccess] = useState<string | null>(null);
  const [notificationPreferences, setNotificationPreferences] = useState<AdminNotificationPreference[]>([]);
  const [savingNotificationPrefs, setSavingNotificationPrefs] = useState(false);
  const [notificationPrefSuccess, setNotificationPrefSuccess] = useState<string | null>(null);
  const [notificationPrefError, setNotificationPrefError] = useState<string | null>(null);
  const globalNotifCheckboxRef = useRef<HTMLInputElement | null>(null);
  const emailNotifCheckboxRef = useRef<HTMLInputElement | null>(null);
  const smsNotifCheckboxRef = useRef<HTMLInputElement | null>(null);
  const inAppNotifCheckboxRef = useRef<HTMLInputElement | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [confirmManagerRole, setConfirmManagerRole] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'ADMIN',
    companyId: '',
    isActive: true,
    screenSaverLock: false
  });

  const roles = [
    { value: 'SUPER_ADMIN', label: 'Super Admin' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'COMPANY_ADMIN', label: 'Admin Entreprise' },
    { value: 'SHOP_ADMIN', label: 'Admin Shop' },
    { value: 'MANAGER', label: 'Manager (une boutique, sans suppression)' }
  ];

  useEffect(() => {
    if (id) {
      fetchAdmin();
      fetchCompanies();
      fetchShops();
    }
  }, [id]);

  const fetchAdmin = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/admins/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        const admin = data.data.admin;
        setFormData({
          name: admin.name,
          email: admin.email,
          role: admin.role,
          companyId: admin.companyId || '',
          isActive: admin.isActive,
          screenSaverLock: admin.permissions?.screenSaverLock === true
        });
        if (Array.isArray(admin.assignedShopIds)) {
          setAssignedShopIds(admin.assignedShopIds);
        }
        if (Array.isArray(admin.notificationPreferences)) {
          setNotificationPreferences(admin.notificationPreferences);
        }
      } else {
        setError(data?.message || 'Admin non trouvé');
        navigate('/admins');
      }
    } catch (err) {
      console.error('Erreur chargement admin:', err);
      setError('Erreur de connexion au serveur');
      navigate('/admins');
    } finally {
      setFetching(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/companies'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.data.companies);
      }
    } catch (err) {
      console.error('Erreur chargement entreprises:', err);
    }
  };

  const fetchShops = async () => {
    try {
      const token = localStorage.getItem('token');
      // allShops=true: this page needs the full shop catalog, not just the admin's current shop
      const response = await fetch(getApiUrl('/api/admin/shops?limit=1000&allShops=true'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setShops(data.data?.shops || []);
      }
    } catch (err) {
      console.error('Erreur chargement boutiques:', err);
    }
  };

  const toggleShop = (shopId: string) => {
    const shop = shops.find((item) => item.id === shopId);
    setAssignedShopIds((prev) => {
      const isRemoving = prev.includes(shopId);
      const next = isRemoving
        ? prev.filter((item) => item !== shopId)
        : [...prev, shopId];

      if (!isRemoving && shop?.companyId && formData.role !== 'SUPER_ADMIN') {
        setFormData((current) => ({
          ...current,
          companyId: shop.companyId || current.companyId,
        }));
      }

      return next;
    });
  };

  const selectSingleShop = (shopId: string) => {
    if (!shopId) {
      setAssignedShopIds([]);
      setFormData((current) => ({
        ...current,
        email: current.role === 'MANAGER' ? '' : current.email,
      }));
      return;
    }

    const shop = shops.find((item) => item.id === shopId);
    setAssignedShopIds([shopId]);
    if (shop) {
      setFormData((current) => ({
        ...current,
        companyId: shop.companyId || current.companyId,
        ...(current.role === 'MANAGER' ? { email: shop.email || '' } : {}),
      }));
    }
  };

  const visibleShops =
    formData.role === 'SUPER_ADMIN'
      ? shops
      : formData.companyId
        ? shops.filter((shop) => shop.companyId === formData.companyId)
        : shops;

  const handleSaveShops = async () => {
    try {
      setSavingShops(true);
      setShopSuccess(null);
      const token = localStorage.getItem('token');

      const response = await fetch(getApiUrl(`/api/admin/admins/${id}/shops`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ shopIds: assignedShopIds })
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setShopSuccess('Boutiques mises à jour');
        if (data.data?.companyId) {
          setFormData((current) => ({
            ...current,
            companyId: data.data.companyId,
          }));
        }
        setTimeout(() => setShopSuccess(null), 3000);
      } else {
        setError(data?.message || 'Erreur lors de la mise à jour des boutiques');
      }
    } catch (err) {
      console.error('Erreur mise à jour boutiques:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setSavingShops(false);
    }
  };

  const handleNotificationPrefToggle = (
    eventType: AdminNotificationPreference['eventType'],
    channel: 'emailEnabled' | 'smsEnabled' | 'inAppEnabled',
    value: boolean,
  ) => {
    setNotificationPreferences((prev) =>
      prev.map((pref) => (pref.eventType === eventType ? { ...pref, [channel]: value } : pref)),
    );
  };

  const handleSaveNotificationPreferences = async () => {
    try {
      setSavingNotificationPrefs(true);
      setNotificationPrefSuccess(null);
      setNotificationPrefError(null);
      setError(null);
      setErrorDetails([]);
      const token = localStorage.getItem('token');

      const response = await fetch(getApiUrl(`/api/admin/admins/${id}/notification-preferences`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preferences: notificationPreferences }),
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setNotificationPrefSuccess('Préférences de notifications mises à jour');
        setTimeout(() => setNotificationPrefSuccess(null), 3000);
      } else {
        const message = data?.message || 'Erreur lors de la mise à jour des préférences de notifications';
        setNotificationPrefError(message);
        setError(message);
      }
    } catch (err) {
      console.error('Erreur mise à jour préférences notifications:', err);
      setNotificationPrefError('Erreur de connexion au serveur');
      setError('Erreur de connexion au serveur');
    } finally {
      setSavingNotificationPrefs(false);
    }
  };

  const areAllNotificationChannelsChecked =
    notificationPreferences.length > 0 &&
    notificationPreferences.every((pref) => pref.emailEnabled && pref.smsEnabled && pref.inAppEnabled);
  const isSomeNotificationChannelsChecked =
    notificationPreferences.some((pref) => pref.emailEnabled || pref.smsEnabled || pref.inAppEnabled) &&
    !areAllNotificationChannelsChecked;

  const areAllEmailChecked =
    notificationPreferences.length > 0 &&
    notificationPreferences.every((pref) => pref.emailEnabled);
  const isSomeEmailChecked =
    notificationPreferences.some((pref) => pref.emailEnabled) && !areAllEmailChecked;
  const areAllSmsChecked =
    notificationPreferences.length > 0 &&
    notificationPreferences.every((pref) => pref.smsEnabled);
  const isSomeSmsChecked =
    notificationPreferences.some((pref) => pref.smsEnabled) && !areAllSmsChecked;
  const areAllInAppChecked =
    notificationPreferences.length > 0 &&
    notificationPreferences.every((pref) => pref.inAppEnabled);
  const isSomeInAppChecked =
    notificationPreferences.some((pref) => pref.inAppEnabled) && !areAllInAppChecked;

  useEffect(() => {
    if (globalNotifCheckboxRef.current) {
      globalNotifCheckboxRef.current.indeterminate = isSomeNotificationChannelsChecked;
    }
    if (emailNotifCheckboxRef.current) {
      emailNotifCheckboxRef.current.indeterminate = isSomeEmailChecked;
    }
    if (smsNotifCheckboxRef.current) {
      smsNotifCheckboxRef.current.indeterminate = isSomeSmsChecked;
    }
    if (inAppNotifCheckboxRef.current) {
      inAppNotifCheckboxRef.current.indeterminate = isSomeInAppChecked;
    }
  }, [
    isSomeNotificationChannelsChecked,
    isSomeEmailChecked,
    isSomeSmsChecked,
    isSomeInAppChecked,
  ]);

  const handleToggleAllNotificationChannels = (checked: boolean) => {
    setNotificationPrefSuccess(null);
    setNotificationPrefError(null);
    setNotificationPreferences((prev) =>
      prev.map((pref) => ({
        ...pref,
        emailEnabled: checked,
        smsEnabled: checked,
        inAppEnabled: checked,
      })),
    );
  };

  const handleToggleAllForChannel = (
    channel: 'emailEnabled' | 'smsEnabled' | 'inAppEnabled',
    checked: boolean,
  ) => {
    setNotificationPrefSuccess(null);
    setNotificationPrefError(null);
    setNotificationPreferences((prev) =>
      prev.map((pref) => ({
        ...pref,
        [channel]: checked,
      })),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || (formData.role !== 'MANAGER' && !formData.email)) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (formData.role === 'MANAGER' && assignedShopIds.length !== 1) {
      setError('Un Manager doit être assigné à une et une seule boutique');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      const token = localStorage.getItem('token');

      if (formData.role === 'MANAGER') {
        const shopsResponse = await fetch(getApiUrl(`/api/admin/admins/${id}/shops`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ shopIds: assignedShopIds })
        });
        const shopsData = await shopsResponse.json().catch(() => null);
        if (!shopsResponse.ok || !shopsData?.success) {
          setError(shopsData?.message || 'Impossible d’assigner la boutique au Manager');
          return;
        }
      }
      
      const response = await fetch(getApiUrl(`/api/admin/admins/${id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive,
          companyId: formData.companyId || null,
          permissions: { screenSaverLock: formData.screenSaverLock }
        })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const reason = data?.message || "Erreur lors de la mise à jour de l'admin";
        setError(reason);
        setErrorDetails(Array.isArray(data?.errors) ? data.errors : []);
        return;
      }

      navigate('/admins');
    } catch (err) {
      console.error('Erreur mise à jour admin:', err);
      setError('Erreur de connexion au serveur');
      setErrorDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setPasswordError('Veuillez remplir tous les champs');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/admins/${id}/password`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setPasswordSuccess('Mot de passe modifié avec succès');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess(null);
        }, 1500);
      } else {
        setPasswordError(data?.message || 'Erreur lors du changement de mot de passe');
      }
    } catch (err) {
      console.error('Erreur changement mot de passe:', err);
      setPasswordError('Erreur de connexion au serveur');
    }
  };

  /** Login of a MANAGER is imposed by the shop, so switching role rewrites the email. */
  const managerLoginShop = shops.find((shop) => shop.id === assignedShopIds[0]);

  const applyManagerRole = () => {
    setAssignedShopIds((current) => current.slice(0, 1));
    setFormData((prev) => ({
      ...prev,
      role: 'MANAGER',
      email: managerLoginShop?.email || '',
    }));
    setConfirmManagerRole(false);
  };

  const handleInputChange = (field: string, value: any) => {
    if (field === 'role' && value === 'MANAGER') {
      if (formData.role !== 'MANAGER') setConfirmManagerRole(true);
      return;
    }

    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === 'companyId' && value && prev.role !== 'SUPER_ADMIN') {
        setAssignedShopIds((current) =>
          current.filter((shopId) => {
            const shop = shops.find((item) => item.id === shopId);
            return shop?.companyId === value;
          }),
        );
      }

      if (field === 'role' && value === 'SUPER_ADMIN') {
        next.screenSaverLock = false;
      }

      return next;
    });
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="dt-list-toolbar flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admins')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <span>Modifier l'Admin</span>
              <HelpIcon 
                docPath="09-gestion-administrateurs#09-5-modification" 
                tooltip="Documentation : Modification d'un administrateur"
              />
            </h1>
            <p className="text-gray-600">Modifier les informations de l'administrateur</p>
          </div>
        </div>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center space-x-2"
        >
          <Key className="h-4 w-4" />
          Changer le mot de passe
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Nom complet *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="input-field pl-10"
                  placeholder="Nom complet"
                  autoComplete="off"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="input-field pl-10"
                  placeholder={formData.role === 'MANAGER' ? 'Email de la boutique sélectionnée' : 'email@example.com'}
                  autoComplete="off"
                  required={formData.role !== 'MANAGER'}
                  disabled={formData.role === 'MANAGER'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Rôle *
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner un rôle</option>
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Entreprise
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={formData.companyId}
                  onChange={(e) => handleInputChange('companyId', e.target.value)}
                  className="input-field"
                >
                  <option value="">Aucune entreprise</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500">
                {formData.role === 'SUPER_ADMIN'
                  ? 'Laisser vide pour les Super Admins'
                  : formData.role === 'MANAGER'
                    ? 'Sélectionnez exactement une boutique ci-dessous : l’entreprise est alignée automatiquement.'
                    : 'Obligatoire lorsqu\'une boutique est assignée. Doit correspondre à l\'entreprise de la boutique.'}
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Statut
              </label>
              <select
                value={formData.isActive ? 'true' : 'false'}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                className="input-field"
              >
                <option value="true">Actif</option>
                <option value="false">Inactif</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Permissions
              </label>
              <label className="flex min-h-[42px] items-center gap-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.screenSaverLock}
                  onChange={(e) => handleInputChange('screenSaverLock', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={formData.role === 'SUPER_ADMIN'}
                />
                <span>Screen saver</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/admins')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>

      {/* Shop Assignments */}
      {shops.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Store className="h-5 w-5 text-gray-500" />
                Boutiques assignées
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {formData.role === 'MANAGER'
                  ? "Sélectionnez exactement une boutique. Son email sera utilisé comme identifiant de connexion."
                  : "L’admin recevra les notifications de réservation uniquement pour les boutiques cochées. Une boutique ne peut être assignée que si l’admin est rattaché à la même entreprise."}
              </p>
            </div>
          </div>

          {formData.role === 'MANAGER' ? (
            <select
              value={assignedShopIds[0] || ''}
              onChange={(e) => selectSingleShop(e.target.value)}
              className="input-field max-w-md"
              required
            >
              <option value="">Sélectionner une boutique</option>
              {visibleShops.map((shop) => (
                <option key={shop.id} value={shop.id}>{shop.name}</option>
              ))}
            </select>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visibleShops.map(shop => (
                <label
                  key={shop.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    assignedShopIds.includes(shop.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={assignedShopIds.includes(shop.id)}
                    onChange={() => toggleShop(shop.id)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">{shop.name}</span>
                </label>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleSaveShops}
              disabled={savingShops}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingShops ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{savingShops ? 'Enregistrement...' : 'Enregistrer les boutiques'}</span>
            </button>
            {shopSuccess && (
              <span className="text-sm text-green-600 font-medium">{shopSuccess}</span>
            )}
          </div>
        </div>
      )}

      {notificationPreferences.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Bell className="h-5 w-5 text-gray-500" />
                Notifications par type
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Activez/désactivez les notifications Email, SMS et In-App pour chaque type d&apos;événement.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                ref={globalNotifCheckboxRef}
                type="checkbox"
                checked={areAllNotificationChannelsChecked}
                onChange={(e) => handleToggleAllNotificationChannels(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              Tout cocher / décocher
            </label>
          </div>

          <div className="overflow-x-auto">
            <ResponsiveTable className="min-w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    <div className="inline-flex items-center gap-2">
                      <input
                        ref={emailNotifCheckboxRef}
                        type="checkbox"
                        checked={areAllEmailChecked}
                        onChange={(e) => handleToggleAllForChannel('emailEnabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span>Email</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    <div className="inline-flex items-center gap-2">
                      <input
                        ref={smsNotifCheckboxRef}
                        type="checkbox"
                        checked={areAllSmsChecked}
                        onChange={(e) => handleToggleAllForChannel('smsEnabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span>SMS</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    <div className="inline-flex items-center gap-2">
                      <input
                        ref={inAppNotifCheckboxRef}
                        type="checkbox"
                        checked={areAllInAppChecked}
                        onChange={(e) => handleToggleAllForChannel('inAppEnabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span>In-App</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {notificationPreferences.map((pref) => (
                  <tr key={pref.eventType}>
                    <td className="px-4 py-3 text-sm text-gray-800">{notificationEventLabels[pref.eventType]}</td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={pref.emailEnabled}
                        onChange={(e) => handleNotificationPrefToggle(pref.eventType, 'emailEnabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={pref.smsEnabled}
                        onChange={(e) => handleNotificationPrefToggle(pref.eventType, 'smsEnabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={pref.inAppEnabled}
                        onChange={(e) => handleNotificationPrefToggle(pref.eventType, 'inAppEnabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>

          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleSaveNotificationPreferences}
              disabled={savingNotificationPrefs}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingNotificationPrefs ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{savingNotificationPrefs ? 'Enregistrement...' : 'Enregistrer les notifications'}</span>
            </button>
            {notificationPrefSuccess && (
              <span className="text-sm text-green-600 font-medium">{notificationPrefSuccess}</span>
            )}
            {notificationPrefError && (
              <span className="text-sm text-red-600 font-medium">{notificationPrefError}</span>
            )}
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Changer le mot de passe</h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError(null);
                    setPasswordSuccess(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fermer</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800">{passwordError}</p>
                </div>
              )}

              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-800">{passwordSuccess}</p>
                </div>
              )}

              <form onSubmit={handlePasswordChange} autoComplete="off" className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Mot de passe actuel *
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="input-field pl-10"
                      placeholder="Mot de passe actuel"
                      autoComplete="off"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Nouveau mot de passe *
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="input-field pl-10"
                      placeholder="Nouveau mot de passe"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500">Minimum 8 caractères</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Confirmer le nouveau mot de passe *
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="input-field pl-10"
                      placeholder="Confirmer le nouveau mot de passe"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Changer le mot de passe
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmManagerRole}
        title="Passer ce compte en Manager ?"
        message="Un Manager gère une seule boutique et se connecte obligatoirement avec l'email de cette boutique. Son identifiant actuel sera donc remplacé."
        confirmLabel="Changer le rôle"
        confirmVariant="warning"
        onConfirm={applyManagerRole}
        onClose={() => setConfirmManagerRole(false)}
      >
        <div className="space-y-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Identifiant actuel</p>
            <p className="mt-1 text-sm text-gray-900">{formData.email || '—'}</p>
          </div>
          {managerLoginShop?.email ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-amber-700">
                Nouvel identifiant (boutique {managerLoginShop.name})
              </p>
              <p className="mt-1 text-sm text-amber-900">{managerLoginShop.email}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Aucune boutique avec email n'est assignée. L'email sera vidé : sélectionnez une boutique
              dans « Boutiques assignées » avant d'enregistrer.
            </div>
          )}
          <p className="text-xs text-gray-500">
            Le changement n'est appliqué qu'après avoir cliqué sur « Mettre à jour ».
          </p>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default AdminEditPage;
